import { clone, filter, find, isEqual } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
    Component, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';

import { PickerComponent } from '../picker/picker.component';
import { PickerTranslations } from '@shared/utils/translations';
import { BaseControlValueAccessor } from './base-control-value-accessor';

@Component({
    selector: 'syn-multi-picker',
    templateUrl: './multi-picker.component.html',
    styleUrls: ['./multi-picker.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => MultiPickerComponent),
            multi: true
        }
    ]
})
export class MultiPickerComponent<T> extends BaseControlValueAccessor<T> implements ControlValueAccessor, OnInit, OnDestroy {

    @Input() translations: PickerTranslations;
    @Input() placeholder: string;
    @Input() contentLoader: Subject<{ term: string; strict?: boolean }>;
    @Input() content: Observable<T[]>;
    @Input() itemTemplate: TemplateRef<any>;
    @Input() newItemTemplate: TemplateRef<any>;
    @Input() errorMessage: string;
    @Input() required: object;
    @Input() chipTemplate: TemplateRef<any>;
    @Input() strictSearch: boolean;
    @Input() chipsPosition: 'top' | 'bottom' = 'top';
    @Input() unicityCriteria: string;
    @Input() disabled = false;

    @Output() creationEvent = new EventEmitter<string>();
    @Output() change = new EventEmitter<T[]>();

    @ViewChild('picker', { static: true }) picker: PickerComponent<T>;

    public objectPickerControl: FormControl;

    private destroy$ = new Subject();

    constructor() {
        super();
    }

    public async ngOnInit(): Promise<void> {
        this.objectPickerControl = new FormControl();
        this.objectPickerControl.valueChanges.pipe(
            takeUntil(this.destroy$)
        ).subscribe(object => {
            if (!object) {
                return;
            }
            this.addObject(object);
        });

        this.picker.select.openedChange.pipe(
            takeUntil(this.destroy$)
        ).subscribe(async (state) => {
            if (!state && !this.value) {
                this.checkValidity();
            }
        });
    }

    public ngOnDestroy(): void {
        this.destroy$.next(true);
    }

    public writeValue(objects: T[]): void {
        if (objects && objects.length === 0) {
            objects = null;
        }

        this.value = objects;
        this.onChange(this.value);
        this.onTouched();
        this.change.emit(this.value);
        this.checkValidity();
    }

    public setDisabledState(isDisabled: boolean): void {
        let objectPickerStatus = isDisabled ? this.objectPickerControl.disable() : this.objectPickerControl.enable();
        objectPickerStatus = undefined;
        this.disabled = isDisabled;
        return objectPickerStatus;
    }

    public onCreateObject($event: string): void {
        this.creationEvent.emit($event);
    }

    public addObject(object: T) {
        this.picker.selectedValue = null;

        const alreadySelected = this.unicityCriteria ?
            find(this.value, o1 => isEqual(o1[this.unicityCriteria], object[this.unicityCriteria])) :
            find(this.value, o1 => isEqual(o1, object));
        if (alreadySelected) {
            return;
        }

        const objects = this.value || [];
        objects.push(clone(object));
        this.writeValue(objects);
    }

    public removeObject(object: T) {
        this.writeValue(filter(this.value, o1 => !isEqual(o1, object)));
    }

    private checkValidity(): void {
        if (this.required !== undefined && (!this.value || !this.value.length)) {
            this.picker.select.ngControl.control.setErrors({ required: true });
        }
    }
}
